<template>
  <div>
    <div ref="scroll" class="scroll" :style="{ transform: `translate3D(0, -${progress.px}px, 0)` }">
      <header class=header>
        <div class="header-container">
          <h1 ref="title">{{ current.name }}</h1>
          <h2 ref="subtitle">{{ subtitle }}</h2>
        </div>
      </header>
      <div class="gallery" v-images-loaded:on.progress="imageProgress">
        <div class="gallery-container">
          <div class="gallery-image"
            ref="images"
            v-for="i in current.nbImages"
            v-in-viewport
            :key="i">
            <img :src="require(`@/assets/gallery/${current.url}/${i}.jpg`)" :alt="current.name">
          </div>
        </div>
      </div>
      <footer class="footer">
        <router-link 
          @mouseenter.native="$emit('mouse', 'hover')" 
          @mouseleave.native="$emit('mouse', 'normal')" 
          :to="{ path: `/${next.url}` }" 
          class="footer-container">
          <img :src="require(`@/assets/home/${next.url}.jpg`)" :alt="next.name">
          <div>
            <h3>{{ next.name }}</h3>
            <p>Explore</p>
          </div>
        </router-link>
      </footer>
    </div>
  </div>
</template>

<script>
import routes from '../routes'

export default {
  name: 'gallery',
  props: {
    progress: {
      type: Object
    },
    winsize: {
      type: Object
    },
  },
  data() {
    return {
      loadedImage: 0,
      intersectOpts: {
        callback: this.visibilityChanged,
        intersection: {
          rootMargin: '20px',
        },
      }
    }
  },
  computed: {
    current() {
      return routes[this.$route.meta.index]
    },
    maxImage() {
      return this.current.nbImages
    },
    next() {
      return routes.length - 1 == this.$route.meta.index ? routes[0] : routes[this.$route.meta.index + 1]
    },
    loadedImageProgress() {
      return (this.loadedImage / this.maxImage) * 100
    },
    subtitle() {
     return this.loadedImage == this.maxImage ? 'Scroll' : `${this.loadedImage} / ${this.maxImage}`
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.$refs.scroll.style.opacity = 1
      vm.$anime({
        targets: [vm.$refs.title, vm.$refs.subtitle],
        translateY: [-10, 0],
        opacity: [0, 1],
        duration: 1200,
        delay: vm.$anime.stagger(1000, {start: 2000}),
        easing: 'cubicBezier(0.520, 0.340, 0.005, 0.995)',
      })
    })
  },
  beforeRouteLeave (to, from , next) {
    this.$anime({
      targets: this.$refs.scroll,
      opacity: [1, 0],
      duration: 1200,
      delay: 300,
      easing: 'cubicBezier(0.520, 0.340, 0.005, 0.995)',
      complete: () => {
        this.$emit('scrollTo', { y: 0, now: true })
        this.$emit('load', false)
        this.$refs.images.map(image => {
          image.classList.add('below-viewport')
          image.classList.remove('in-viewport')
          image.classList.remove('above-viewport')
        })
        this.loadedImage = 0
        next()
      }
    })
  },
  methods: {
    imageProgress(instance, image ) {
      if (image.isLoaded) {
        let className = image.img.naturalWidth > image.img.naturalHeight ? 'paysage' : 'portrait'
        image.img.classList.add(className)
        this.loadedImage++
        if (this.loadedImage == this.maxImage) this.$emit('load', true)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.header{
  position: relative;
  z-index: 1;
  height: calc(var(--vh, 1vh) * 100);
  display: flex;
  align-items: center;
  justify-content: center;

  &-container {
    @extend %container;
    text-align: center;
  }

  h1 {
    @extend %title;
  }
  
  h2, 
  p {
     @extend %subtitle;
  }
}

.footer {
  position: relative;
  z-index: 1;
  height: calc(var(--vh, 1vh) * 100);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  &-container {
    @extend %container;
    display: flex;
    position: relative;
    z-index: 1;
    height: 200px;
    margin-top: -$margin;
    align-items: center;
    justify-content: center;
    text-decoration: none;

    @media screen and (min-width: 768px) {
      height: 400px;
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      z-index: 0;
      opacity: 0.8;
    }

    h3 {
      @extend %title;
      position: relative;
      z-index: 1;
    }

    p {
      @extend %subtitle;
      opacity: 0;
      transition: opacity 0.5s $ease-out;
      color: $darkvanilla;
    }

    &:hover p {
      opacity: 1;
    }
  }
}

.gallery {
  display: flex;
  justify-content: center;

  @media screen and (max-width: 768px) {
    margin: 0 $margin;
  }

  &-container {
    @extend %container;
  }

  &-image {
    z-index: 1;
    display: flex;
    align-items: flex-start;
    margin-bottom: $margin;

    @media screen and (min-width: 768px) {
      margin-bottom: 10%;
      &:nth-of-type(odd) {
        justify-content: flex-end;
      }

      &:nth-last-of-type(1n+1) {
        img {
          margin-left: 5%;
          margin-right: 10%;
        }
      }

      &:nth-last-of-type(2n+1) {
        img {
          margin-left: 12.5%;
          margin-right: 7.5%;
        }
      }

      &:nth-of-type(3n + 1) {
        img {
          margin-left: 2.5%;
          margin-right: 15%;
        }
      }
    }

    &.in-viewport {
      img {
        opacity: 1;
        transform: scale(1);
      }
    }   
    
    img {
      opacity: 0;
      transform: scale(1.05);
      transition: transform 1200ms cubic-bezier(0.520, 0.340, 0.005, 0.995), opacity 1200ms cubic-bezier(0.520, 0.340, 0.005, 0.995);
      width: 100%;
      height: auto;

      @media screen and (min-width: 768px) {
        &.portrait {
          width: 30%;
        }

        &.paysage {
          width: 40%;
        }
      }
    }
  }
}
</style>